import { config } from '../config';

export interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AssistantMapLayerInfo {
  id: string;
  name: string;
  type: string;
  visible: boolean;
}

/** Live, ephemeral map state when the user is viewing a map. */
export interface AssistantMapContext {
  center?: { lat: number; lng: number };
  zoom?: number;
  renderer?: string;
  layerCount?: number;
  selectedLayerIds?: string[];
  visibleLayers?: AssistantMapLayerInfo[];
  hiddenLayers?: AssistantMapLayerInfo[];
}

/** Where the user currently is in the app, so the agent has page awareness. */
export interface AssistantContext {
  /** Current route path, e.g. "/" or "/map/next/<id>". */
  path?: string;
  /** The map id when the user is viewing a map. */
  mapId?: string;
  /** Live map state (viewport, layers, selection) when on a map. */
  map?: AssistantMapContext;
}

/**
 * Start an assistant chat turn. Returns the raw streaming `Response` whose body
 * is a Server-Sent Events stream of `AgentEvent` JSON objects. The caller is
 * responsible for reading and parsing the stream.
 *
 * Auth is cookie-based (`credentials: 'include'`), matching the rest of the SDK.
 */
export const chatStream = (
  messages: AssistantMessage[],
  opts: {
    signal?: AbortSignal;
    context?: AssistantContext;
    conversationId?: string;
    /** Ops-only base-model override for this turn. Rejected with 403 otherwise. */
    model?: string;
  } = {},
): Promise<Response> => {
  const url = `${config.API_URL}/v1/assistant/chat`;
  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      messages,
      ...(opts.context ? { context: opts.context } : {}),
      ...(opts.conversationId ? { conversationId: opts.conversationId } : {}),
      ...(opts.model ? { model: opts.model } : {}),
    }),
    signal: opts.signal,
  });
};

/**
 * Post a screenshot of the current map view back to a waiting agent turn
 * (BIR-145). The agent hands out a one-shot token with its capture request and
 * blocks on this arriving.
 *
 * Raw bytes, not base64 JSON — a third smaller on the wire, and it keeps the
 * chat route's own JSON body limit where it is.
 */
export const postCapture = (token: string, image: Blob): Promise<Response> =>
  fetch(`${config.API_URL}/v1/assistant/capture/${token}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': image.type || 'image/jpeg' },
    body: image,
  });

/**
 * Models an ops user may switch the assistant to. Returns an empty list for
 * everyone else, so the caller can render the control on a non-empty list
 * rather than checking ops itself.
 */
export const listModels = (): Promise<Response> =>
  fetch(`${config.API_URL}/v1/assistant/models`, {
    credentials: 'include',
  });

export interface AssistantConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
  turnCount: number;
}

/** The caller's recent assistant conversations, newest first. */
export const listConversations = (): Promise<Response> =>
  fetch(`${config.API_URL}/v1/assistant/conversations`, {
    credentials: 'include',
  });

/** Rehydrate one conversation as `{ id, messages: [{role, content}] }`. */
export const getConversation = (id: string): Promise<Response> =>
  fetch(`${config.API_URL}/v1/assistant/conversations/${id}`, {
    credentials: 'include',
  });
